import { useEffect } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { FeedbackType } from '@weave/schema-gen-ts/dist/shared/call-intelligence/enums.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CallIntelMutations } from '@frontend/api-call-intel';
import { getUser, isWeaveUser } from '@frontend/auth-helpers';
import { Feedback, FeedbackModal } from '@frontend/call-intel-core';
import { i18next, useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { useIsSmallMode } from '@frontend/shared';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { StarIcon, TextLink, useModalControl, useAlert, useTooltip, Button } from '@frontend/design-system';
import { trackingIds } from '../tracking-ids';

type FeedbackButtonProps = {
  containerStyles?: Interpolation<Theme>;
  disableWeaveUserCheck?: boolean;
  label?: string;
  modalSubtitle?: string;
  modalTitle?: string;
  productCode?: Feature;
  productName?: string;
  responsive?: boolean;
  showAllowToContact?: boolean;
  showFeedbackIcon?: boolean;
  showModal?: boolean;
  trackingId?: string;
  trackingIdModalBase?: string;
};

export const FeedbackButton = ({
  containerStyles,
  disableWeaveUserCheck = false,
  label,
  modalSubtitle,
  modalTitle,
  productCode,
  productName,
  responsive,
  showAllowToContact,
  showFeedbackIcon = true,
  showModal = false,
  trackingId,
  trackingIdModalBase,
}: FeedbackButtonProps) => {
  const { t } = useTranslation('analytics');
  const alert = useAlert();
  const { modalProps, triggerProps, openModal } = useModalControl();
  const { selectedLocationIds } = useAppScopeStore();
  const { tooltipProps, triggerProps: toolTipTriggerProps, Tooltip } = useTooltip();
  const { isSmallMode } = useIsSmallMode();

  const isDisabledForWeaveUser = !disableWeaveUserCheck && isWeaveUser();

  const { isLoading, mutateAsync: submitFeedback } = CallIntelMutations.useSubmitFeedback({
    options: {
      onSuccess: () => {
        alert.success(t('Feedback submitted. Thank you!'));
        modalProps.onClose();
      },
      onError: (error) => {
        alert.error(t('Error submitting feedback. Please try again.'));
        sentry.warn({
          error: `Error submitting feedback: ${error}`,
          team: 'analytics',
          topic: 'call-intelligence',
        });
      },
    },
  });

  const resolvedProductName = productName ?? getProductName(productCode);
  const resolvedTrackingIdModalBase = trackingIdModalBase ?? trackingIds.callIntel.generalFeedback;
  const resolvedLabel = label ?? t('Submit Feedback');

  const handleSubmit = (feedback: Feedback) => {
    if (selectedLocationIds.length === 0) {
      alert.error(t('Error submitting feedback. Please try again.'));
      return;
    }

    submitFeedback({
      rating: feedback.rating,
      comment: feedback.message,
      allowWeaveToContact: feedback.allowWeaveToContact,
      userId: getUser()?.userID || '',
      feedbackType: FeedbackType.FEEDBACK_GENERAL,
      locationId: selectedLocationIds[0],
    });
  };

  useEffect(() => {
    if (showModal) openModal();
  }, [showModal]);

  return (
    <>
      <div {...toolTipTriggerProps} css={containerStyles}>
        {responsive && isSmallMode ? (
          <Button
            iconName='user-voice'
            variant='secondary'
            {...triggerProps}
            css={styles.iconButton}
            trackingId={trackingId}
            disabled={isDisabledForWeaveUser}
            aria-label={resolvedLabel}
            data-testid='feedback-button-icon'
          />
        ) : (
          <TextLink
            {...triggerProps}
            disabled={isDisabledForWeaveUser}
            css={styles.button}
            trackingId={trackingId}
            weight='bold'
            data-testid='feedback-button-text'
          >
            {showFeedbackIcon && <StarIcon size={16} />}
            {resolvedLabel}
          </TextLink>
        )}

        {isDisabledForWeaveUser && (
          <Tooltip {...tooltipProps}>
            {t(
              'This feature is intended solely for Weave customers to provide feedback on AI outputs perceived as wrongly classified. If you are not a Weave customer, we advise you to skip this module.'
            )}
          </Tooltip>
        )}
      </div>
      <FeedbackModal
        isLoading={isLoading}
        modalProps={modalProps}
        productName={resolvedProductName}
        onSubmit={handleSubmit}
        showAllowToContact={showAllowToContact}
        subtitle={modalSubtitle}
        title={modalTitle}
        trackingIdBase={resolvedTrackingIdModalBase}
      />
    </>
  );
};

// Maps Feature enum to display name
const getProductName = (productCode?: Feature): string => {
  if (productCode === Feature.CALL_INTELLIGENCE) {
    return i18next.t('Call Intelligence', { ns: 'call-intelligence' });
  }
  return '';
};

const styles = {
  button: css`
    align-items: center;
    display: flex;
    gap: ${theme.spacing(0.5)};
  `,
  iconButton: css`
    padding: ${theme.spacing(0.5)};
    svg {
      width: 24px;
      height: 24px;
    }
  `,
};
